/// <amd-module name="Release/Landing/HubContent/AnalyticsPivot" />
import * as React from "react";
import { createUrlForRoute } from "Release/Helpers/Link";
import { endScenario, PerfScenarios, startScenario } from "Release/Helpers/Performance";
import * as Resources from "Release/Landing/Resources";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { ClientContribution } from "VSS/Platform/Contracts/Contributions";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssComponentProperties, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { Spinner } from "VSSUI/Spinner";

import { SelectPipelineView } from "./SelectPipelineView";
import { publishClickEvent } from "Release/Helpers/Telemetry";

export interface IAnalyticsPivotProps extends IVssComponentProperties {
    pipelineId: number;
    selectedFolderPath: string;
    isTabSwitchNavigation: boolean;
    markTTICallback: () => void;
}

interface IAnalyticsContributionOptions {
    onDefinitionChanged: (handler: (definitionId: number | undefined) => void) => void;
}

const releaseReportsContributionId = "ms.vss-releaseManagement-web.report-catalog";

export class AnalyticsPivot extends VssComponent<IAnalyticsPivotProps, {}>{
    constructor(props: IAnalyticsPivotProps, context: IVssComponentContext) {
        super(props, context);

        startScenario(context.pageContext, PerfScenarios.AnalyticsPivot, false);
    }

    public componentDidMount(): void {
        setTimeout(() => {
            this._endPerfScenario();
        }, 0);
    
        this._publishAnalyticsTabAccessEvent();
    }
    
    public componentWillReceiveProps(newProps: IAnalyticsPivotProps) {
        if (!newProps.isTabSwitchNavigation && newProps.pipelineId !== this.props.pipelineId) {
            // Start perf scenario on selection change in leftPanel
            startScenario(this.context.pageContext, PerfScenarios.AnalyticsPivot, false);
            this._analyticsMarked = false;
        }

        const newPipelineId: number = newProps.pipelineId;
        if (typeof newPipelineId === "number" && newPipelineId > 0 && newPipelineId !== this.props.pipelineId && this._pipelineChangedHandlerMap) {
            for (let key in this._pipelineChangedHandlerMap) {
                if (this._pipelineChangedHandlerMap.hasOwnProperty(key)) {
                    this._pipelineChangedHandlerMap[key](newPipelineId);
                }
            }
        }
    }

    public componentDidUpdate(prevProps: IAnalyticsPivotProps, prevState: {}) {
        if (!this.props.isTabSwitchNavigation && prevProps.pipelineId !== this.props.pipelineId && !this._analyticsMarked) {
            endScenario(this.context.pageContext, PerfScenarios.AnalyticsPivot, false);
            this._analyticsMarked = true;
        }
    }

    public render(): React.ReactNode {
        if (this.props.selectedFolderPath) {
            // TODO - wait for spec on view to be shown on folder selection
            setTimeout(() => {
                this._endPerfScenario();
            }, 0);
            return (<SelectPipelineView resourceText={Resources.AnalyticsLowerCase} />);
        }

        if (typeof this.props.pipelineId !== "number" || this.props.pipelineId <= 0) {
            return (<SelectPipelineView resourceText={Resources.AnalyticsLowerCase} />);
        }

        if (!AnalyticsPivot._contributions) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const contributionIds = contributionService.getContributionChildren(releaseReportsContributionId) || [];
            AnalyticsPivot._contributions = contributionIds.map((cid) => {
                // Construct a contribution object that ContributionContentHost will undertstand
                const contributionProperties = contributionService.getContribution<any>(cid);
                return {
                    id: cid,
                    properties: contributionProperties
                } as ClientContribution;
            });
        }

        const onCardClick = (contribution: ClientContribution) => {
            let url = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.cd-release-report-default-route", {
                "definitionId": this.props.pipelineId.toString(10),
                "_a": contribution.properties.reportcontributionid as string
            });

            FastPageSwitch(this.context.pageContext, url, true);
        };

        const onNewReleaseCardClick = (contribution: ClientContribution, routeId: string) => {
            let url = createUrlForRoute(this.context.pageContext, routeId, {
                "definitionId": this.props.pipelineId.toString(10),
                "contextType": contribution.properties.context as string
            });

            FastPageSwitch(this.context.pageContext, url, true);
        }


        const loadingComponent = () => <Spinner className={"flex flex-grow loading-analytics"} />;

        return (
            <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                < div className="analytics-pivot-content">
                    {
                        AnalyticsPivot._contributions.map((contribution: ClientContribution) => (
                            <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                                {contribution.properties.isLWP ?
                                    <RegionComponent
                                        className="flex-row"
                                        key={"release-analytics-" + contribution.id}
                                        componentRegion="pipelineAnalyticsRegion"
                                        childComponentProperties={{
                                            "definitionId": this.props.pipelineId,
                                            "contextType": contribution.properties.context,
                                            onCardClick: (routeId: string) => onNewReleaseCardClick(contribution, routeId)
                                        }}
                                    />
                                    :
                                    <div
                                        className={"analytics-card cursor-pointer"}
                                        key={"release-analytics-" + contribution.id}
                                        onClick={() => onCardClick(contribution)}>
                                        <LegacyComponent
                                            modules={["VSSPreview/Providers/ContentHost"]}
                                            wrappedType={"vssLegacyContributionContentHost"}
                                            wrappedProps={{
                                                className: "analytics-contribution-host",
                                                context: {
                                                    onDefinitionChanged: (handler: (definitionId: number | undefined) => void) => this._pipelineChangedHandlerMap[contribution.id] = handler,
                                                    detachDefinitionChanged: () => delete this._pipelineChangedHandlerMap[contribution.id]
                                                } as IAnalyticsContributionOptions,
                                                contribution: contribution,
                                                loadingComponent: loadingComponent
                                            }}
                                            loadingComponent={loadingComponent}
                                            dependentContributionIds={["ms.vss-web.legacy-vss-ui-content"]}
                                            expectRegisteredInstance={true}
                                        />
                                    </div>
                                }
                            </ErrorBoundary>))
                    }
                </div>
            </ErrorBoundary>);
    }

    private _endPerfScenario = () => {
        // End ReleasesHub perf scenario when Analytics loading is complete
        if (!this.props.isTabSwitchNavigation) {
            this.props.markTTICallback();
        }

        //End AnalyticsPivot scenario when -
        // 1. there is a selection change in left panel
        // 2. or initial page load is complete
        // 3. or Right pivot change
        if (!this._analyticsMarked) {
            endScenario(this.context.pageContext, PerfScenarios.AnalyticsPivot, false);
            this._analyticsMarked = true;
        }
    }

    private _publishAnalyticsTabAccessEvent(): void {
        publishClickEvent(this.context.pageContext, "ReleaseManagement", "Releases", {
            "PipleineId":this.props.pipelineId.toString(),
            "Scenario": "AnalyticsTabAccess"
        });
    }

    private _pipelineChangedHandlerMap: { [key: string]: (definitionId: number | undefined) => void } = {};
    private static _contributions: ClientContribution[];
    private _analyticsMarked: boolean = false;
}
